import { del, get, post, put } from '@/services/http'
import { endpoints } from '@/services/endpoints'

/**
 * Documents & SOPs — the knowledge register. LIVE.
 *
 * The whole resource: `POST`, `GET` (collection and detail), `PUT`, `DELETE`.
 *
 * ── A document, a revision and an attachment are three tables ────────────
 * `Document` is the logical record — name, type, effective and expiry dates.
 * `DocumentRevision` is one immutable row per version of the FILE, numbered from
 * 1, each with its own presigned view URL and its own ingestion status.
 * `DocumentAttachment` is one row per thing the document is linked to, and a
 * document may be linked to any mix of plants, lines, machines, asset classes
 * and sensors at once.
 *
 * Nothing overwrites a revision. Sending a different `document_file_id` on the
 * update adds revision N+1, so the history stays queryable — which is also why
 * there is no "revision" field a person can type.
 *
 * ── This file is the seam ────────────────────────────────────────────────
 * The type list below is the server's `DocumentType`, exported from here and
 * imported by the screens. It is NOT the demo's list — three of the eight had
 * different names, and a value this app invented would be a 422 on the first
 * real write. See `DOC_TYPES`.
 *
 * ── What the collection does NOT serve ───────────────────────────────────
 * The pickers. There is no target list on this payload, so the form reads the
 * hierarchy, the asset-class library and the sensor collection directly — the
 * same shape `WorkOrdersPage` uses, and the right one anyway: folding the whole
 * estate into every page of a register would send it fifty rows at a time.
 */

/**
 * `DocumentType`, all eight, in the order the chip strip lists them.
 *
 * ── Three were renamed, and they are the server's names now ──────────────
 * The demo said `manual`, `sop` and `layout`; the enum says `oem_manual`,
 * `procedure` and `floor_plan`. The other five already agreed. This list is the
 * wire value throughout — nothing translates between two spellings, because a
 * mapping table is a second place for them to drift.
 */
export const DOC_TYPES = [
  'oem_manual',
  'procedure',
  'drawing',
  'datasheet',
  'certificate',
  'policy',
  'report',
  'floor_plan',
]

/**
 * What a document can be attached to — the five FKs on `DocumentAttachment`,
 * and the five `*_ids` lists on the create and update payloads.
 *
 * `plant` and `machine` are the API's names for what this product calls a SITE
 * and an ASSET — see `features/hierarchy/api.js` on the two vocabularies. The
 * wire value is what lives here; the label is the i18n layer's problem.
 */
export const ATTACH_KINDS = ['plant', 'line', 'machine', 'asset_class', 'sensor']

/**
 * `DocumentLevel` — the DEEPEST place a document may be attached, shallowest first.
 *
 * A `line` document attaches to plants and lines but never a machine; a
 * `machine` one attaches anywhere. `verify_attachment_changes` refuses a target
 * ranked below the level, so the picker offers only what the level allows.
 * Set on create and immutable after, like the owner plant.
 */
export const DOC_LEVELS = ['plant', 'line', 'machine']

/** `_LEVEL_RANK` server-side. A target is attachable when its rank ≤ the level's. */
export const LEVEL_RANK = { plant: 0, line: 1, machine: 2 }

/**
 * The rank of an attachment target, or `null` when it has none.
 *
 * Places rank by tier. A sensor takes the rank of what it sits on — the picker
 * only lists sensors by machine, so every sensor it offers is a 2. An asset
 * class ranks by its `entity_type`; a SENSOR class has no rank and any level
 * may take it (`_CLASS_RANK` has no `sensor` key).
 */
export const rankOf = (kind, entityType) => {
  if (kind === 'asset_class') return LEVEL_RANK[entityType] ?? null
  if (kind === 'sensor') return LEVEL_RANK.machine
  return LEVEL_RANK[kind] ?? null
}

/** `DocumentRevisionStatus` — the AI engine's ingestion state, not a sign-off. */
export const REVISION_STATUSES = ['uploaded', 'processed', 'failed']

/** `Field(min_length=1, max_length=255)` on `name`. */
export const NAME_MAX_LENGTH = 255

/** `error` on a failed revision — `Field(max_length=500)`. */
export const REVISION_ERROR_MAX_LENGTH = 500

/** The largest page the server will serve — `Query(..., le=200)`. */
export const MAX_PAGE_SIZE = 200

/** What the register asks for. */
export const DEFAULT_PAGE_SIZE = 50

/** The two views. Same records, different density. */
export const DOC_VIEWS = ['list', 'cards']

/*
 * ── The DEMO's scope ladder, and why it is not here ─────────────────────
 * `DOC_SCOPES = ['unplaced', 'instance', 'line', 'site', 'enterprise']` was the
 * organising idea of the whole demo screen: a document's scope was the
 * SHALLOWEST tier any of its links pointed at, the register had a column for
 * it, and the upload wizard used it to decide which tier you were allowed to
 * link to.
 *
 * The API has no scope, and two of those five values cannot exist at all:
 *
 *   ENTERPRISE means "attached to nothing, applies to everything".
 *   `at_least_one_attachment` refuses a document with no attachments.
 *   UNPLACED means "nobody has decided yet", which is the same zero-attachment
 *   state, and is what Contextualisation was built on.
 *
 * The other three are derivable, but only from the attachments a row already
 * carries — and the list item carries `attachment_count` and not the
 * attachments, so the register could not compute it without a request per row.
 * Filtering by it could not go to the server either, so it would narrow the
 * loaded page and quietly disagree with page two.
 *
 * So scope is gone rather than approximated, and the register shows what a
 * document is ATTACHED TO, which is the fact the API actually holds. Recorded
 * in BACKEND-GAPS.md.
 *
 * export const DOC_SCOPES = ['unplaced', 'instance', 'line', 'site', 'enterprise']
 * export const DOC_LINK_KINDS = ['asset', 'line', 'site']
 */

/**
 * Wire → app.
 *
 * The list item and the detail share every field but three: `attachments` and
 * `revisions` are detail-only, and the list sends `attachment_count` in their
 * place. Both go through here so a row from either shape is the same object
 * with the absent halves empty.
 *
 * ── What is NOT here, because `DocumentOut` does not carry it ────────────
 * The owner, the tags, the language, the source system, the page count, the
 * file size, and a human-readable revision label. The demo had all seven. None
 * is a column. They are listed in BACKEND-GAPS.md rather than invented here.
 */
const toDocument = (row) => {
  const revisions = (row.revisions ?? []).map((revision) => ({
    id: revision.id,
    /* An INTEGER the server assigns, not a label somebody types. Revision 1 is
       created with the document and every later one is added by a `PUT` that
       carried a different file. */
    revision: revision.revision,
    fileId: revision.document_file_id,
    /* A presigned address with a short life, resolved per request server-side.
       It must not be cached past the response that carried it, which is why the
       detail query below sets no long `staleTime`. */
    viewUrl: revision.document_view_url,
    /* `uploaded` → the bytes are stored and nothing has read them yet.
       `processed` → the AI engine has ingested it. `failed` → it has not, and
       `error` says why in a sentence meant for a person. This is INGESTION
       state and not a human sign-off; see the note on `verified` below. */
    status: revision.status,
    error: revision.error ?? null,
    createdAt: revision.created_at ?? null,
  }))

  return {
    id: row.id,
    name: row.name,
    documentType: row.document_type,

    /* The OWNER plant, not an attachment. `null` means the document belongs to
       the whole account and may be attached anywhere in it; a plant id limits
       every attachment to that plant. Immutable after create. */
    ownerPlantId: row.plant_id ?? null,
    /* The deepest tier it may be attached at — see `DOC_LEVELS`. */
    level: row.level,

    /* Calendar dates, `YYYY-MM-DD`. Not instants: both are `date` columns and
       the day is the whole of the fact, so formatting either against a timezone
       would move it for half the estate. */
    effectiveDate: row.effective_date ?? null,
    expiryDate: row.expiry_date ?? null,

    /* The highest revision number, served on both shapes. On a list row it is
       the only thing said about the file at all. */
    currentRevision: row.current_revision ?? 0,

    /* Two shapes of one fact, for the same reason a logbook row carries
       `photo_count` and a detail carries `photos`. */
    attachmentCount: row.attachment_count ?? (row.attachments?.length ?? 0),
    attachments: (row.attachments ?? []).map((attachment) => ({
      kind: attachment.entity_type,
      id: attachment.entity_id,
      name: attachment.name,
    })),

    revisions,
    /* The one a reader means by "the document". Last in the list, because
       `_revision_outs` orders ascending by number. */
    latestRevision: revisions.length ? revisions[revisions.length - 1] : null,

    createdAt: row.created_at ?? null,
    updatedAt: row.updated_at ?? null,
  }
}

/*
 * ── `verified` had no column, and the pill now shows something else ──────
 * The demo's register carried a Verified flag, a filter for it, a count of
 * unverified documents on the header, and a `PATCH {verified: true}` that its
 * own comment called "the only write on the screen". None of it exists
 * server-side.
 *
 * The nearest REAL fact is the latest revision's `status`, which is shown
 * instead and named for what it is: whether the engine has ingested the file.
 * That is not the same claim as "a person has checked this revision", so it is
 * not relabelled as one. The human sign-off is in BACKEND-GAPS.md.
 *
 * export const useVerifyDoc = () => …  // PATCH is not even a method here
 */

/**
 * The register.
 *
 * Params are snake_case because this is a real FastAPI signature and a
 * camelCase key is silently IGNORED — the server would answer the unfiltered
 * page 1 and the screen would show the wrong rows under the right heading.
 *
 * ── The type counts are taken before the type filter ─────────────────────
 * `counts.document_type` is computed over the entity-scoped, searched set
 * BEFORE `document_type` applies. So each chip carries the number it would
 * narrow to, and pressing one moves the rows without rewriting the strip — the
 * same arrangement as the work-order board's column headers.
 *
 * ── `search` matches the NAME and nothing else ───────────────────────────
 * `apply_search(query, search, Document.name)`. The demo's box also matched the
 * id, the owner and the tags; two of those three are not columns and the third
 * is not searched. The screen says so beside the box rather than letting
 * somebody type an owner and read the empty result as "no such document".
 *
 * @param {{
 *   documentType?: string, level?: string, ownerPlantId?: string,
 *   plantId?: string, lineId?: string, machineId?: string,
 *   assetClassId?: string, sensorId?: string, search?: string,
 *   sortBy?: string, sortDir?: string, page?: number, pageSize?: number,
 * }} params
 */
export async function fetchDocuments(params = {}, signal) {
  const {
    documentType,
    level,
    ownerPlantId,
    plantId,
    lineId,
    machineId,
    assetClassId,
    sensorId,
    search,
    sortBy = 'created_at',
    sortDir = 'desc',
    page = 1,
    pageSize = DEFAULT_PAGE_SIZE,
  } = params

  const data = await get(endpoints.documents.list, {
    signal,
    params: {
      // `all` is this screen's sentinel for an unset filter; the wire wants the
      // key absent, not the word.
      ...(documentType && documentType !== 'all' ? { document_type: documentType } : {}),
      ...(level ? { level } : {}),
      // The OWNER filter. `plant_id` below is the attachment filter — two
      // different questions about the same plant.
      ...(ownerPlantId ? { owner_plant_id: ownerPlantId } : {}),
      ...(plantId ? { plant_id: plantId } : {}),
      ...(lineId ? { line_id: lineId } : {}),
      ...(machineId ? { machine_id: machineId } : {}),
      ...(assetClassId ? { asset_class_id: assetClassId } : {}),
      ...(sensorId ? { sensor_id: sensorId } : {}),
      ...(search?.trim() ? { search: search.trim() } : {}),
      sort_by: sortBy,
      sort_dir: sortDir,
      page,
      page_size: pageSize,
    },
  })

  return {
    items: (data?.documents ?? []).map(toDocument),
    // Echoed back by the server rather than assumed from what was asked for: a
    // clamped or defaulted page must move the footer, not be contradicted by it.
    page: data?.page ?? page,
    pageSize: data?.page_size ?? pageSize,
    total: data?.total ?? 0,
    totalPages: data?.total_pages ?? 0,
    counts: data?.counts?.document_type ?? {},
  }
}

/** One document, with its attachments and its whole revision history. */
export async function fetchDocument(id, signal) {
  return toDocument(await get(endpoints.documents.detail(id), { signal }))
}

/**
 * App → wire, shared by the create and the update.
 *
 * `DocumentCreate` and `DocumentUpdate` share these nine fields with no
 * defaults between them, so this is a full replace either way and a key left
 * out is a 400 rather than "leave it alone" — the same contract `ShiftUpdate`
 * has. The create adds two more, `plant_id` and `level`; see `createDocument`.
 *
 * The five id lists are `[]` when empty and never null, which the schema says
 * out loud. At least one of them must be non-empty: `at_least_one_attachment`
 * is a Pydantic `model_validator`, so it answers FastAPI's bare 422 shape
 * rather than a named field — a refusal from it arrives keyed `body`, with
 * nothing to attach to a control. The form guarantees it before sending.
 */
const toWire = ({
  name,
  documentType,
  effectiveDate,
  expiryDate,
  attachments,
  documentFileId,
}) => ({
  name: name.trim(),
  document_type: documentType,
  effective_date: effectiveDate,
  // Nullable, and therefore still required to be PRESENT — omitting a nullable
  // key is a 422 in this API.
  expiry_date: expiryDate || null,
  plant_ids: idsOf(attachments, 'plant'),
  line_ids: idsOf(attachments, 'line'),
  machine_ids: idsOf(attachments, 'machine'),
  asset_class_ids: idsOf(attachments, 'asset_class'),
  sensor_ids: idsOf(attachments, 'sensor'),
  document_file_id: documentFileId,
})

/**
 * The screens hold attachments as one flat list of `{kind, id}`, because that is
 * what a picker produces and what a chip row renders. The wire wants five
 * separate arrays. Split here, once, rather than in each caller.
 */
const idsOf = (attachments, kind) =>
  (attachments ?? []).filter((one) => one.kind === kind).map((one) => one.id)

/**
 * File one.
 *
 * ── `document_file_id` must be a CONFIRMED upload of purpose `document` ───
 * `_load_document_file` looks for exactly that and 400s on `document_file_id`
 * otherwise. So the three-step handshake in `features/files/api.js` has to have
 * completed before this is called — there is no "create the record now and
 * attach the file later" path.
 *
 * ── Identical content is REFUSED, and the file is destroyed with it ───────
 * The server hashes the bytes and looks for the same hash already attached to
 * any of the targets being requested. On a match it calls `storage.reject_file`
 * and 400s on `document_file_id`. The file id is then dead: the object has been
 * deleted, so retrying the same create with the same id fails differently. The
 * caller has to make the person pick the file again, which is why
 * `isDuplicateContent` exists below.
 */
export async function createDocument({ ownerPlantId, level, ...input }) {
  return toDocument(
    await post(endpoints.documents.list, {
      ...toWire(input),
      /* CREATE-ONLY, and both required. `null` is account-wide and must still be
         SENT — omitting a nullable key is a 400. The update must not carry
         either: unknown body fields are refused, and `DocumentUpdate` has
         neither. */
      plant_id: ownerPlantId ?? null,
      level,
    }),
  )
}

/**
 * Save one.
 *
 * A FULL replace, including the attachments and the file. Passing the CURRENT
 * revision's `document_file_id` leaves the file alone; passing a different one
 * adds revision N+1. There is no other way to spell either, so the form has to
 * know which file the document is on before it can save a name change — which
 * is why `useDocument` keeps no stale cache.
 *
 * The owner plant and level are NOT sent: `toWire` picks its nine keys, and
 * `DocumentUpdate` would refuse either as an unknown field.
 */
export async function saveDocument({ id, ...input }) {
  return toDocument(await put(endpoints.documents.detail(id), toWire(input)))
}

/** Remove one. Soft-deletes the record and detaches every revision's file. */
export async function deleteDocument(id) {
  await del(endpoints.documents.detail(id))
  return { id, deleted: true }
}

/**
 * Whether a refusal is the duplicate-content one.
 *
 * Matched on the FIELD rather than the sentence: the message is the server's
 * English and this app does not render it, but `document_file_id` is the key it
 * arrives under and is stable. It matters because this one failure is not
 * retryable — the uploaded object is gone, so the dialog must send the person
 * back to the file step rather than let them press Save again.
 */
export const isDuplicateContent = (error) => Boolean(error?.fields?.document_file_id)
